import { BubbleMenu } from '@tiptap/react/menus'
import type { Editor } from '@tiptap/react'
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Divider,
  Paper,
  Tooltip,
} from '@mui/material'
import {
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  WrapText,
  Delete,
} from '@mui/icons-material'
import type { ImageAlignment, ImageFloat } from './types'

interface ImageBubbleMenuProps {
  editor: Editor
}

export function ImageBubbleMenu({ editor }: ImageBubbleMenuProps) {
  // Get current image attributes
  const getImageAttrs = () => {
    const { selection } = editor.state
    const node = editor.state.doc.nodeAt(selection.from)
    if (node?.type.name === 'resizableImage') {
      return node.attrs
    }
    return null
  }

  const attrs = getImageAttrs()
  const currentAlignment = attrs?.alignment || 'center'
  const currentFloat = attrs?.float || 'none'

  const handleAlignmentChange = (
    _event: React.MouseEvent<HTMLElement>,
    newAlignment: ImageAlignment | null
  ) => {
    if (newAlignment) {
      editor.chain().focus().setImageAlignment(newAlignment).run()
    }
  }

  const handleFloatChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFloat: ImageFloat | null
  ) => {
    if (newFloat !== null) {
      editor.chain().focus().setImageFloat(newFloat).run()
    }
  }

  const handleDelete = () => {
    editor.chain().focus().deleteSelection().run()
  }

  return (
    <BubbleMenu
      editor={editor}
      options={{
        placement: 'top',
        offset: { mainAxis: 10 },
      }}
      shouldShow={({ editor, state }) => {
        const { selection } = state
        const node = state.doc.nodeAt(selection.from)
        return node?.type.name === 'resizableImage' && editor.isEditable
      }}
    >
      <Paper
        elevation={8}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          p: 0.5,
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        {/* Alignment controls */}
        <ToggleButtonGroup
          value={currentFloat === 'none' ? currentAlignment : null}
          exclusive
          onChange={handleAlignmentChange}
          size="small"
          aria-label="image alignment"
        >
          <ToggleButton value="left" aria-label="align left">
            <Tooltip title="Align left">
              <FormatAlignLeft fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="center" aria-label="align center">
            <Tooltip title="Align center">
              <FormatAlignCenter fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="right" aria-label="align right">
            <Tooltip title="Align right">
              <FormatAlignRight fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Float/wrap controls */}
        <ToggleButtonGroup
          value={currentFloat}
          exclusive
          onChange={handleFloatChange}
          size="small"
          aria-label="text wrap"
        >
          <ToggleButton value="none" aria-label="no wrap">
            <Tooltip title="No text wrap">
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: 'bold',
                }}
              >
                ━
              </Box>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="left" aria-label="wrap right">
            <Tooltip title="Float left (text wraps right)">
              <WrapText fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="right" aria-label="wrap left">
            <Tooltip title="Float right (text wraps left)">
              <WrapText fontSize="small" sx={{ transform: 'scaleX(-1)' }} />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Delete button */}
        <Tooltip title="Delete image">
          <IconButton
            size="small"
            onClick={handleDelete}
            color="error"
            sx={{ p: 0.5 }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
    </BubbleMenu>
  )
}
